import { createSlice } from "@reduxjs/toolkit"
import type { PayloadAction } from "@reduxjs/toolkit"
import type { Dispatch } from "redux"
import {
  clearAllCheckoutAddressSessions,
  clearCheckoutAddressSessionsForAddress,
  readCheckoutAddressSession,
  writeCheckoutAddressSession,
} from "@/domain/address/checkout-address-session"

/**
 * Checkout address selection — the ordering-session choice, kept apart from the raw
 * customer address list in `auth.user.enrichment.addresses`.
 *
 * Runtime source of truth. sessionStorage (see checkout-address-session) is only a
 * restoration mirror written by the thunks below. Not redux-persisted.
 * Address GET responses never write this slice.
 */
export interface CheckoutAddressState {
  selectedAddressId: string | null
}

const initialState: CheckoutAddressState = {
  selectedAddressId: null,
}

function normalizeAddressId(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed ? trimmed : null
}

export const checkoutAddressSlice = createSlice({
  name: "checkoutAddress",
  initialState,
  reducers: {
    selectAddress: (state, action: PayloadAction<string>) => {
      state.selectedAddressId = normalizeAddressId(action.payload)
    },
    clearAddress: (state) => {
      state.selectedAddressId = null
    },
    /** Same effect as selectAddress; distinct type so a restore is distinguishable from a tap. */
    restoreAddress: (state, action: PayloadAction<string>) => {
      state.selectedAddressId = normalizeAddressId(action.payload)
    },
  },
})

export const { selectAddress, clearAddress, restoreAddress } = checkoutAddressSlice.actions
export default checkoutAddressSlice.reducer

type SessionScope = {
  userId: string | null | undefined
  restaurantId: string | null | undefined
}

type StateWithCheckoutAddress = {
  checkoutAddress?: CheckoutAddressState
}

/** User selection: Redux first, then the restoration mirror. */
export const selectCheckoutAddressForSession =
  (input: SessionScope & { addressId: string }) =>
  (dispatch: Dispatch): void => {
    const addressId = normalizeAddressId(input.addressId)
    if (!addressId) return
    dispatch(selectAddress(addressId))
    if (input.userId && input.restaurantId) {
      writeCheckoutAddressSession(input.restaurantId, { userId: input.userId, addressId })
    }
  }

/**
 * New-document restoration. Restores only when nothing is selected yet and the stored entry
 * matches BOTH the current customer and the current restaurant. Returns the restored id.
 */
export const restoreCheckoutAddressForSession =
  (scope: SessionScope) =>
  (dispatch: Dispatch, getState: () => StateWithCheckoutAddress): string | null => {
    const userId = normalizeAddressId(scope.userId)
    const restaurantId = normalizeAddressId(scope.restaurantId)
    if (!userId || !restaurantId) return null
    if (getState().checkoutAddress?.selectedAddressId) return null
    const stored = readCheckoutAddressSession(restaurantId)
    if (!stored || stored.userId !== userId) return null
    dispatch(restoreAddress(stored.addressId))
    return stored.addressId
  }

/**
 * Controlled initialization for a customer with exactly one saved address: select it so
 * checkout does not ask them to choose the only option. Guarded on the live store state
 * (not a render closure): never runs over an existing explicit or restored selection, and
 * never fires for zero or multiple addresses — those keep the existing rules.
 * Returns the id when it initialized the selection.
 */
export const initializeOnlySavedAddressForSession =
  (input: SessionScope & { addressIds: ReadonlyArray<string | null | undefined> }) =>
  (dispatch: Dispatch, getState: () => StateWithCheckoutAddress): string | null => {
    if (getState().checkoutAddress?.selectedAddressId) return null
    if (input.addressIds.length !== 1) return null
    const addressId = normalizeAddressId(input.addressIds[0])
    if (!addressId) return null
    dispatch(selectAddress(addressId))
    if (input.userId && input.restaurantId) {
      writeCheckoutAddressSession(input.restaurantId, { userId: input.userId, addressId })
    }
    return addressId
  }

/**
 * A saved address was deleted server-side. The mirror entries that point at it are dropped
 * for every restaurant so it can never be restored into a later document, and the runtime
 * selection is cleared only when it is that address — another customer choice is never
 * disturbed. Returns true when the live selection was the deleted address.
 *
 * Call only after the server has confirmed the deletion.
 */
export const clearCheckoutAddressForDeletedAddress =
  (input: { addressId: string }) =>
  (dispatch: Dispatch, getState: () => StateWithCheckoutAddress): boolean => {
    const addressId = normalizeAddressId(input.addressId)
    if (!addressId) return false
    clearCheckoutAddressSessionsForAddress(addressId)
    if (getState().checkoutAddress?.selectedAddressId !== addressId) return false
    dispatch(clearAddress())
    return true
  }

/** Logout / session invalidation: Redux and every restaurant's mirror entry. */
export const clearCheckoutAddressForSession =
  () =>
  (dispatch: Dispatch): void => {
    dispatch(clearAddress())
    clearAllCheckoutAddressSessions()
  }
